import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, MessageCircle, Phone, Calendar, Calculator, MapPin, Share2, ChevronLeft, ChevronRight } from "lucide-react";
import api from "../lib/api";
import { formatPriceINR, formatKm, coverImage, resolveImageUrl, buildWhatsAppLink } from "../lib/format";
import { useSiteSettings } from "../lib/hooks";
import VehicleCard from "../components/VehicleCard";
import SafeImage from "../components/SafeImage";

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between py-3 border-b hairline text-sm">
    <span className="text-[#242424]/70">{label}</span>
    <span className="text-[#111111] font-medium text-right">{value || "—"}</span>
  </div>
);

const CarDetail = () => {
  const { slug } = useParams();
  const nav = useNavigate();
  const [v, setV] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [similar, setSimilar] = useState([]);
  const [idx, setIdx] = useState(0);
  const settings = useSiteSettings();

  useEffect(() => {
    setV(null); setNotFound(false); setIdx(0);
    api.get(`/vehicles/${slug}`).then((r) => setV(r.data)).catch(() => setNotFound(true));
    api.get(`/vehicles/${slug}/similar`).then((r) => setSimilar(r.data)).catch(() => {});
  }, [slug]);

  if (notFound) {
    return (
      <div className="container-wide py-24 text-center" data-testid="car-not-found">
        <div className="font-display text-3xl font-semibold">Vehicle not found</div>
        <Link to="/cars" className="mt-4 inline-block text-[#7A1519] underline">Back to inventory</Link>
      </div>
    );
  }
  if (!v) return <div className="container-wide py-24 text-sm text-[#242424]/60">Loading…</div>;

  const images = v.images?.length ? v.images : [{ url: "", storage_path: "" }];
  const currentImg = images[idx]?.url ? resolveImageUrl(images[idx].url) : coverImage(v);
  const share = async () => {
    try {
      await navigator.share?.({ title: `${v.brand} ${v.model}`, url: window.location.href });
    } catch {}
  };
  const wa = settings?.whatsapp ? buildWhatsAppLink(
    settings.whatsapp,
    `Hi Autobahn Cars, I'm interested in the ${v.manufacturing_year} ${v.brand} ${v.model} ${v.variant || ""}. Link: ${window.location.href}`
  ) : null;

  return (
    <div data-testid="page-car-detail" className="pb-24">
      <div className="container-wide pt-6">
        <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm text-[#242424]/70 hover:text-[#7A1519]" data-testid="back-btn">
          <ArrowLeft size={14} /> Back
        </button>
      </div>

      <div className="container-wide grid grid-cols-1 lg:grid-cols-12 gap-10 mt-8">
        <div className="lg:col-span-8">
          <div className="relative aspect-[16/10] bg-[#111111] overflow-hidden rounded-sm">
            <SafeImage
              src={currentImg}
              alt={`${v.brand} ${v.model}`}
              className="w-full h-full object-cover"
            />
            {v.status !== "available" && (
              <div className="absolute top-4 left-4 text-xs uppercase tracking-[0.22em] font-semibold px-3 py-1.5 rounded-sm bg-white text-[#242424]">
                {v.status.replace("_", " ")}
              </div>
            )}
            {images.length > 1 && (
              <>
                <button onClick={() => setIdx((idx - 1 + images.length) % images.length)} className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-sm" data-testid="gallery-prev"><ChevronLeft size={18} /></button>
                <button onClick={() => setIdx((idx + 1) % images.length)} className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-sm" data-testid="gallery-next"><ChevronRight size={18} /></button>
              </>
            )}
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 mt-3 overflow-x-auto">
              {images.map((im, i) => (
                <button key={i} onClick={() => setIdx(i)} className={`shrink-0 w-24 aspect-[4/3] overflow-hidden rounded-sm border-2 ${idx === i ? "border-[#7A1519]" : "border-transparent"}`} data-testid={`gallery-thumb-${i}`}>
                  <SafeImage src={resolveImageUrl(im.url)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          {v.description && (
            <div className="mt-12" data-testid="detail-description">
              <div className="overline text-[#7A1519] mb-3">About this car</div>
              <p className="text-[#242424] leading-relaxed whitespace-pre-line">{v.description}</p>
            </div>
          )}

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-10">
            <div>
              <div className="overline text-[#7A1519] mb-3">Specifications</div>
              <Row label="Manufacturer" value={v.brand} />
              <Row label="Model" value={v.model} />
              <Row label="Variant" value={v.variant} />
              <Row label="Manufacturing Year" value={v.manufacturing_year} />
              <Row label="Registration Year" value={v.registration_year} />
              <Row label="Registration State" value={v.registration_state} />
              <Row label="Kilometres" value={formatKm(v.kilometres)} />
              <Row label="Fuel" value={v.fuel} />
              <Row label="Transmission" value={v.transmission} />
              <Row label="Ownership" value={v.ownership} />
            </div>
            <div>
              <div className="overline text-[#7A1519] mb-3 mt-8 md:mt-0">Engine & Exterior</div>
              <Row label="Engine" value={v.engine} />
              <Row label="Power" value={v.power} />
              <Row label="Torque" value={v.torque} />
              <Row label="Drivetrain" value={v.drivetrain} />
              <Row label="Body Type" value={v.body_type} />
              <Row label="Exterior Colour" value={v.exterior_colour} />
              <Row label="Interior Colour" value={v.interior_colour} />
              <Row label="Seating Capacity" value={v.seating_capacity} />
              <Row label="Keys Available" value={v.keys_available} />
              <Row label="Insurance Validity" value={v.insurance_validity} />
              {v.custom_specs?.map((c, i) => <Row key={i} label={c.label} value={c.value} />)}
            </div>
          </div>

          {v.features?.length > 0 && (
            <div className="mt-12" data-testid="detail-features">
              <div className="overline text-[#7A1519] mb-4">Features</div>
              <div className="flex flex-wrap gap-2">
                {v.features.map((f, i) => (
                  <span key={i} className="text-xs px-3 py-2 border hairline bg-white rounded-sm">{f}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        <aside className="lg:col-span-4 space-y-6">
          <div className="bg-white border hairline p-8 rounded-sm sticky top-24" data-testid="detail-sidecard">
            <div className="overline text-[#242424]/60">{v.manufacturing_year} · {v.body_type}</div>
            <h1 className="mt-2 font-display text-2xl md:text-3xl tracking-tight font-semibold text-[#111111]">
              {v.brand} {v.model}
            </h1>
            <div className="text-sm text-[#242424]/70 mt-1">{v.variant}</div>
            <div className="mt-6 font-display text-3xl md:text-4xl tracking-tighter font-bold text-[#7A1519]">
              {formatPriceINR(v.price)}
            </div>
            <div className="mt-6 grid grid-cols-3 gap-3 text-center text-xs">
              <div className="border hairline py-3 rounded-sm">
                <div className="text-[#242424]/60">KM</div>
                <div className="text-[#111111] font-semibold mt-1">{formatKm(v.kilometres)}</div>
              </div>
              <div className="border hairline py-3 rounded-sm">
                <div className="text-[#242424]/60">Fuel</div>
                <div className="text-[#111111] font-semibold mt-1">{v.fuel}</div>
              </div>
              <div className="border hairline py-3 rounded-sm">
                <div className="text-[#242424]/60">Trans.</div>
                <div className="text-[#111111] font-semibold mt-1">{v.transmission}</div>
              </div>
            </div>
            <div className="mt-6 flex flex-col gap-2.5">
              <Link to={`/test-drive?vehicle=${v.id}`} className="bg-[#7A1519] hover:bg-[#5a0f12] text-white text-sm font-semibold py-3 rounded-sm inline-flex items-center justify-center gap-2" data-testid="detail-book-test-drive">
                <Calendar size={14} /> Book Test Drive
              </Link>
              {wa && (
                <a href={wa} target="_blank" rel="noreferrer" className="border hairline text-sm py-3 rounded-sm inline-flex items-center justify-center gap-2 hover:bg-[#F7F6F3]" data-testid="detail-whatsapp">
                  <MessageCircle size={14} className="text-[#25D366]" /> WhatsApp Enquiry
                </a>
              )}
              <Link to={`/emi-calculator?price=${v.price}`} className="border hairline text-sm py-3 rounded-sm inline-flex items-center justify-center gap-2 hover:bg-[#F7F6F3]" data-testid="detail-emi">
                <Calculator size={14} /> Estimate EMI
              </Link>
              <div className="flex gap-2 mt-1">
                <a href={`tel:${settings?.phone}`} className="flex-1 border hairline text-xs py-2 rounded-sm inline-flex items-center justify-center gap-1"><Phone size={12} /> Call</a>
                <button onClick={share} className="flex-1 border hairline text-xs py-2 rounded-sm inline-flex items-center justify-center gap-1" data-testid="detail-share"><Share2 size={12} /> Share</button>
              </div>
            </div>
            {settings?.address && (
              <div className="mt-6 pt-6 border-t hairline text-xs text-[#242424]/70">
                <div className="flex items-start gap-2">
                  <MapPin size={12} className="mt-0.5 text-[#7A1519]" />
                  <div>{settings.address}</div>
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>

      {similar.length > 0 && (
        <div className="container-wide mt-24" data-testid="detail-similar">
          <div className="overline text-[#7A1519] mb-3">Similar cars</div>
          <h2 className="font-display text-3xl md:text-4xl tracking-tight font-semibold text-[#111111] mb-10">You may also like</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {similar.map((s) => <VehicleCard key={s.id} vehicle={s} testIdPrefix="similar" />)}
          </div>
        </div>
      )}
    </div>
  );
};

export default CarDetail;
